import { supabase } from '@/integrations/supabase/client'
import { logger } from './logger-service'
import { CaregiverRepository } from '@/lib/repositories/caregiver.repository'

export interface Caregiver {
  id: string
  name: string
  auth_user_id: string
  active: boolean
  last_activity: string
  last_activity_type: string | null
}

export class CaregiverService {
  public static async getCurrentCaregiver(): Promise<Caregiver | null> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      
      if (authError) {
        logger.error('Failed to get current user', { error: authError })
        return null
      }

      if (!user) {
        logger.warn('No user found in session')
        return null
      }

      const caregiver = await CaregiverRepository.getByAuthId(user.id)
      
      if (!caregiver) {
        logger.warn('No caregiver found for user', { userId: user.id })
        return null
      }

      logger.info('Successfully fetched current caregiver', { 
        caregiverId: caregiver.id,
        name: caregiver.name
      })

      return caregiver
    } catch (error) {
      logger.error('Unexpected error getting current caregiver', { error })
      return null
    }
  }

  public static async getCaregiverIdByAuthId(authId: string): Promise<string | null> {
    logger.debug('CaregiverService.getCaregiverIdByAuthId called', {
      component: 'CaregiverService',
      arguments: { authId }
    });
    try {
      const caregiver = await CaregiverRepository.getByAuthId(authId)
      
      if (!caregiver) {
        logger.warn('No caregiver found for user', { userId: authId })
        return null
      }

      logger.info('Successfully fetched caregiver id', { 
        caregiverId: caregiver.id,
        name: caregiver.name
      })

      return caregiver.id
    } catch (error) {
      logger.error('Unexpected error getting caregiver id', { error })
      return null
    }
  }

  private static async _updateLastActivity(caregiverId: string, activityType?: string): Promise<void> {
    try {
      await CaregiverRepository.updateLastActivity(caregiverId, activityType)
      
      logger.info('Successfully updated caregiver activity', {
        caregiverId,
        activityType
      })
    } catch (error) {
      logger.error('Error updating caregiver activity', { error, caregiverId, activityType })
      throw error
    }
  }

  public static async updateLastActivity(caregiverId: string): Promise<void> {
    logger.debug('CaregiverService.updateLastActivity called', {
      component: 'CaregiverService',
      arguments: { caregiverId }
    });
    return CaregiverService._updateLastActivity(caregiverId)
  }

  public static async getCaregiverProfileData(caregiverId: string) {
    logger.debug('CaregiverService.getCaregiverProfileData called', {
      component: 'CaregiverService',
      arguments: { caregiverId }
    });

    try {
      const profileData = await CaregiverRepository.getCaregiverProfileData(caregiverId);
      
      logger.info('Successfully fetched caregiver profile data', {
        caregiverId,
        hasData: !!profileData?.length
      });

      return profileData;
    } catch (error) {
      logger.error('Failed to fetch caregiver profile data', {
        error,
        caregiverId
      });
      throw error;
    }
  }

  public static async deleteAccount(caregiverId: string): Promise<boolean> {
    try {
      logger.debug('CaregiverService.deleteAccount called', {
        component: 'CaregiverService',
        arguments: { caregiverId }
      });

      return await CaregiverRepository.deleteCaregiver(caregiverId)
    } catch (error) {
      logger.error('Failed to delete caregiver account', { 
        error,
        caregiverId 
      })
      throw error
    }
  }
}
